import React, { FC, useContext } from 'react'

import DocumentHead from 'components/DocumentHead'
import DocumentHeader from 'components/DocumentHeader'
import ErrorMessage from 'components/ErrorMessage'

import { ModalContext } from 'containers/ModalManager'
import { useGetTrash, GetTrashProps } from 'modules/shares/operations'

import EmptyState from '../EmptyState'
import LoadingState from '../LoadingState'
import { ShareTrashedModal } from '../ShareTrashedModal'
import { ShareTrashedActions } from '../ShareTrashedActions'

import DocumentItemsLayout from '../DocumentItemsLayout'

interface TrashListProps extends GetTrashProps {
  isUserAdmin: boolean
}

const TrashList: FC<TrashListProps> = props => {
  const { workspaceId, search, isUserAdmin } = props
  const { showModal } = useContext(ModalContext)

  const result = useGetTrash({ workspaceId, search })

  if (!result || result.kind === 'error') {
    return <ErrorMessage.Generic />
  }

  if (result.kind === 'loading') return <LoadingState />

  const { fetchMore, items, loading, totalCount } = result

  if (items.length === 0) {
    return (
      <EmptyState
        search={search}
        title="Trash is empty"
        description="Documents in Trash will be automatically deleted after 90 days, unless you restore them."
        icon="trash-bin"
      />
    )
  }

  return (
    <>
      <DocumentHead title="Trash" />
      <DocumentItemsLayout
        loading={loading}
        shares={items}
        onLoadMore={fetchMore}
        showProjectName
        dropdownMenu={(share, download) => {
          if (share.userAccessLevel !== 'VIEW' || isUserAdmin) {
            return (
              <ShareTrashedActions
                share={share}
                download={download}
                isUserAdmin={isUserAdmin}
              />
            )
          }

          return null
        }}
        totalShareCount={totalCount}
        action={item => {
          if (item.__typename === 'Share') {
            showModal(ShareTrashedModal, {
              share: item,
            })
          }
        }}
        header={
          <DocumentHeader
            title="Trash"
            description="Documents in Trash are deleted after 90 days, unless you restore them."
          />
        }
      />
    </>
  )
}

export default TrashList
